import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Paper,
  InputAdornment,
  CircularProgress,
  Badge,
  Alert,
  useTheme,
  useMediaQuery,
  Drawer,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  List as ListIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ViewList,
  CalendarToday,
  Add,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { Calendar } from 'react-calendar';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import 'react-calendar/dist/Calendar.css';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isSameDay, format } from 'date-fns';
import AddEntryForm from './AddEntryForm';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import StudyCalendar from './StudyCalendar';

const questionTypes = [
  // Reading Comprehension
  'Main Idea',
  'Detail',
  'Inference',
  'Author\'s Purpose',
  'Vocabulary in Context',
  'Text Structure',
  'Compare and Contrast',
  'Evidence Support',
  
  // Critical Reasoning
  'Argument Analysis',
  'Strengthen',
  'Weaken',
  'Assumption',
  'Conclusion',
  'Evidence Evaluation',
  'Logical Flaw',
  
  // Quantitative
  'Problem Solving',
  'Data Analysis',
  'Algebra',
  'Geometry',
  'Statistics',
  'Word Problems',
  'Graphs and Charts',
  
  // Writing
  'Grammar',
  'Sentence Structure',
  'Essay Organization',
  'Rhetorical Skills',
  'Punctuation',
  'Style and Tone',
  
  // General
  'Strategy',
  'Time Management',
  'Other'
];

const WrongAnswerJournal = () => {
  const { currentUser } = useAuth();
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    recentMistakes: 0,
    topTypes: [],
  });
  const [newEntry, setNewEntry] = useState({
    questionNumber: '',
    explanation: '',
    category: '',
    notes: '',
  });
  const [filters, setFilters] = useState({
    questionType: '',
    dateRange: 'all',
    searchQuery: '',
    sortBy: 'date',
  });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, [currentUser]);

  const fetchEntries = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'wrongAnswerJournal'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const entriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(doc.data().date)
      }));

      // Sort entries by date
      entriesData.sort((a, b) => b.date - a.date);
      setEntries(entriesData);
      setFilteredEntries(entriesData);

      // Calculate stats
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const recentEntries = entriesData.filter(entry => entry.date >= sevenDaysAgo);

      // Count question types
      const typeCounts = {};
      entriesData.forEach(entry => {
        if (entry.questionType) {
          typeCounts[entry.questionType] = (typeCounts[entry.questionType] || 0) + 1;
        }
      });

      const sortedTypes = Object.entries(typeCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([name, count]) => ({ name, count }));

      setStats({
        recentMistakes: recentEntries.length,
        topTypes: sortedTypes
      });

    } catch (error) {
      console.error('Error fetching entries:', error);
      setError('Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (formData) => {
    if (!currentUser) return;
    
    try {
      let imageUrl = null;
      
      if (formData.image) {
        const imageRef = ref(storage, `journal-images/${currentUser.uid}/${Date.now()}-${formData.image.name}`);
        await uploadBytes(imageRef, formData.image);
        imageUrl = await getDownloadURL(imageRef);
      }
      
      const now = new Date();
      const entryData = {
        userId: currentUser.uid,
        questionNumber: formData.questionNumber,
        ptNumber: formData.ptNumber,
        sectionNumber: formData.sectionNumber,
        date: now,
        imageUrl: imageUrl || null,
        questionType: formData.questionType,
        details: {
          aiAnalysis: {
            questionType: formData.questionType,
            summary: formData.explanation || '',
            answerAnalysis: formData.notes || ''
          },
          chatHistory: []
        },
        preview: {
          questionType: formData.questionType,
          date: now.toISOString().split('T')[0]
        }
      };
      
      if (isEditing && editingEntry) {
        await updateDoc(doc(db, 'wrongAnswerJournal', editingEntry.id), entryData);
      } else {
        await addDoc(collection(db, 'wrongAnswerJournal'), entryData);
      }
      
      fetchEntries();
      setIsEditing(false);
      setEditingEntry(null);
      setIsAddingEntry(false);
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving entry:', error);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await deleteDoc(doc(db, 'wrongAnswerJournal', entryId));
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const filterEntries = (entries) => {
    return entries.filter(entry => {
      const searchLower = filters.searchQuery.toLowerCase();
      const matchesSearch = 
        !filters.searchQuery ||
        entry.questionNumber?.toString().includes(searchLower) ||
        entry.explanation?.toLowerCase().includes(searchLower) ||
        entry.notes?.toLowerCase().includes(searchLower);

      const matchesType = !filters.questionType || entry.questionType === filters.questionType;

      let matchesDate = true;
      if (filters.dateRange !== 'all') {
        const entryDate = entry.date instanceof Date ? entry.date : new Date(entry.date);
        const now = new Date();
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        
        switch (filters.dateRange) {
          case 'week':
            matchesDate = entryDate >= weekAgo;
            break;
          case 'month':
            matchesDate = entryDate >= monthAgo;
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesType && matchesDate;
    }).sort((a, b) => {
      switch (filters.sortBy) {
        case 'questionNumber':
          return (a.questionNumber || 0) - (b.questionNumber || 0);
        case 'type':
          return (a.questionType || '').localeCompare(b.questionType || '');
        default:
          const dateA = a.date instanceof Date ? a.date : new Date(a.date);
          const dateB = b.date instanceof Date ? b.date : new Date(b.date);
          return dateB - dateA;
      }
    });
  };

  const handleCardClick = (entry) => {
    navigate(`/dashboard/question/${entry.id}`);
  };

  const handleViewQuestion = (questionId) => {
    navigate(`/dashboard/journal/question/${questionId}`);
  };

  // Group entries by date
  const groupEntriesByDate = (entries) => {
    const groups = {};
    entries.forEach(entry => {
      const date = entry.date instanceof Date ? entry.date : new Date(entry.date);
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });
    return Object.entries(groups)
      .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA));
  };

  const handleDateSelect = (date, dateEntries) => {
    setSelectedDate(date);
    setFilteredEntries(dateEntries);
  };

  const renderCalendarView = () => (
    <Box sx={{ width: '70%', margin: 'auto' }}>
      <StudyCalendar 
        entries={entries} 
        onDateSelect={handleDateSelect}
      />
      {selectedDate && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Entries for {selectedDate.toLocaleDateString()}
          </Typography>
          {filteredEntries.length > 0 ? (
            <Stack spacing={2}>
              {filteredEntries.map((entry) => (
                <Card 
                  key={entry.id}
                  onClick={() => handleViewQuestion(entry.id)}
                  sx={{ 
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.shadows[4],
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        PT {entry.ptNumber} • Section {entry.sectionNumber} • Q{entry.questionNumber}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {entry.questionType && (
                        <Chip 
                          label={entry.questionType}
                          size="small" 
                          color="primary"
                        />
                      )}
                      <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                        {entry.initialChoice && (
                          <Typography variant="body2" color="error">
                            Your Answer: {entry.initialChoice}
                          </Typography>
                        )}
                        {entry.correctAnswer && (
                          <Typography variant="body2" color="success.main">
                            Correct: {entry.correctAnswer}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {entry.mistakeReasons && entry.mistakeReasons.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {entry.mistakeReasons.map((reason) => (
                            <Chip 
                              key={reason}
                              label={reason}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No entries for this date
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const renderQuickStats = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6}>
        <Card sx={{ 
          bgcolor: 'background.paper',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="warning" />
                <Typography color="text.secondary">Recent (7 days)</Typography>
              </Box>
              <Typography variant="h4">{stats.recentMistakes}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Card sx={{ 
          bgcolor: 'background.paper',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PsychologyIcon color="info" />
                <Typography color="text.secondary">Most Common Types</Typography>
              </Box>
              <Stack spacing={1}>
                {stats.topTypes.map((type, index) => (
                  <Chip 
                    key={type.name}
                    label={`${index + 1}. ${type.name} (${type.count})`}
                    color={['primary', 'secondary', 'info'][index]}
                    variant="outlined"
                    size="medium"
                    sx={{ justifyContent: 'flex-start', px: 1 }}
                  />
                ))}
                {stats.topTypes.length === 0 && (
                  <Typography color="text.secondary" variant="body2">
                    No question types recorded yet
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderSpeedDial = () => (
    <SpeedDial
      ariaLabel="Journal Actions"
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 }
      }}
      icon={<SpeedDialIcon />}
    >
      <SpeedDialAction
        icon={<Add />}
        tooltipTitle="Add Entry"
        onClick={() => setIsAddingEntry(true)}
      />
      {isMobile && (
        <SpeedDialAction
          icon={<FilterIcon />}
          tooltipTitle="Filters"
          onClick={() => setMobileFilterOpen(true)}
        />
      )}
    </SpeedDial>
  );

  const renderFilterSidebar = () => {
    const filterContent = (
      <Box sx={{ 
        width: isMobile ? '100%' : '280px',
        p: { xs: 2, sm: 3 },
        height: '100%',
        overflow: 'auto',
        bgcolor: theme.palette.mode === 'dark' ? 
          'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)' : 
          'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        borderLeft: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      }}>
        <Stack spacing={3}>
          {/* Quick Stats Card */}
          <Paper sx={{ 
            p: { xs: 2, sm: 3 },
            background: theme.palette.mode === 'dark' ? 
              'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' : 
              'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
            borderRadius: 2,
            color: '#FFFFFF'
          }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon />
                <Typography>Recent (7 days)</Typography>
              </Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '2rem', sm: '2.5rem' }
              }}>
                {stats.recentMistakes}
              </Typography>
            </Stack>
          </Paper>

          {/* Search Box */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B'
            }}>
              Search Entries
            </Typography>
            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              placeholder="Search by question type, number..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.mode === 'dark' ? 
                    'rgba(255,255,255,0.05)' : 
                    'rgba(0,0,0,0.02)',
                }
              }}
            />
          </Box>

          {/* Filters Section */}
          <Paper sx={{ 
            p: { xs: 2, sm: 3 },
            background: theme.palette.mode === 'dark' ? 
              'rgba(59, 130, 246, 0.1)' : 
              'rgba(96, 165, 250, 0.1)',
            borderRadius: 2
          }}>
            <Stack spacing={3}>
              {/* Date Range Filter */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CalendarToday fontSize="small" />
                  Date Range
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {['all', 'week', 'month', 'year'].map((range) => (
                    <Chip
                      key={range}
                      label={range.charAt(0).toUpperCase() + range.slice(1)}
                      onClick={() => handleFilterChange('dateRange', range)}
                      variant={filters.dateRange === range ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        bgcolor: filters.dateRange === range ?
                          (theme.palette.mode === 'dark' ? 
                            'rgba(59, 130, 246, 0.2)' : 
                            'rgba(96, 165, 250, 0.2)'
                          ) : 'transparent',
                        borderColor: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                        color: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 
                            'rgba(59, 130, 246, 0.3)' : 
                            'rgba(96, 165, 250, 0.3)',
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Question Type Filter */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <PsychologyIcon fontSize="small" />
                  Question Types
                </Typography>
                <Stack spacing={1}>
                  {questionTypes.map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => handleFilterChange('questionType', type)}
                      variant={filters.questionType === type ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        justifyContent: 'flex-start',
                        bgcolor: filters.questionType === type ?
                          (theme.palette.mode === 'dark' ? 
                            'rgba(59, 130, 246, 0.2)' : 
                            'rgba(96, 165, 250, 0.2)'
                          ) : 'transparent',
                        borderColor: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                        color: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 
                            'rgba(59, 130, 246, 0.3)' : 
                            'rgba(96, 165, 250, 0.3)',
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* Most Common Types */}
          <Paper sx={{ 
            p: { xs: 2, sm: 3 },
            background: theme.palette.mode === 'dark' ? 
              'rgba(59, 130, 246, 0.1)' : 
              'rgba(96, 165, 250, 0.1)',
            borderRadius: 2
          }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PsychologyIcon color="primary" />
                <Typography sx={{ 
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B'
                }}>
                  Most Common Types
                </Typography>
              </Box>
              <Stack spacing={1}>
                {stats.topTypes.map((type, index) => (
                  <Chip 
                    key={type.name}
                    label={`${index + 1}. ${type.name} (${type.count})`}
                    size="small"
                    sx={{
                      background: theme.palette.mode === 'dark' ? 
                        'rgba(59, 130, 246, 0.2)' : 
                        'rgba(96, 165, 250, 0.2)',
                      color: theme.palette.mode === 'dark' ? '#3B82F6' : '#2563EB',
                      fontWeight: 500,
                      justifyContent: 'flex-start'
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    );

    return isMobile ? (
      <Drawer
        anchor="right"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        PaperProps={{
          sx: {
            width: '85%',
            maxWidth: '360px',
            background: theme.palette.mode === 'dark' ? 
              'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)' : 
              'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          }
        }}
      >
        {filterContent}
      </Drawer>
    ) : (
      filterContent
    );
  };

  const renderListView = (filteredEntries) => {
    // Group entries by date
    const groupedEntries = {};
    filteredEntries.forEach(entry => {
      const entryDate = entry.date instanceof Date ? entry.date : new Date(entry.date);
      const dateKey = entryDate.toDateString();
      if (!groupedEntries[dateKey]) {
        groupedEntries[dateKey] = [];
      }
      groupedEntries[dateKey].push(entry);
    });

    // Sort dates in descending order
    const sortedDates = Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a));

    return (
      <Stack spacing={{ xs: 2, sm: 4 }}>
        {sortedDates.map(dateKey => (
          <Box key={dateKey}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 1.5, sm: 2 }, 
                pb: 1, 
                borderBottom: '2px solid', 
                borderColor: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              {new Date(dateKey).toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {groupedEntries[dateKey].map((entry) => (
                <Grid item xs={12} sm={6} md={4} key={entry.id}>
                  <Card 
                    onClick={() => handleViewQuestion(entry.id)}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: theme.palette.mode === 'dark' ? 
                        'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)' : 
                        'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.palette.mode === 'dark' ? 
                          '0 4px 20px rgba(0, 0, 0, 0.5)' : 
                          '0 4px 20px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start', 
                        mb: 2 
                      }}>
                        <Typography 
                          variant="h6" 
                          component="div"
                          sx={{ 
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B'
                          }}
                        >
                          PT {entry.ptNumber} • Section {entry.sectionNumber} • Q{entry.questionNumber}
                        </Typography>
                        <IconButton
                          size={isMobile ? "small" : "medium"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEntry(entry.id);
                          }}
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? '#94A3B8' : '#475569',
                            '&:hover': {
                              color: theme.palette.error.main
                            }
                          }}
                        >
                          <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        {entry.questionType && (
                          <Chip 
                            label={entry.questionType}
                            size={isMobile ? "small" : "medium"}
                            color="primary"
                            sx={{
                              background: theme.palette.mode === 'dark' ? 
                                'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' : 
                                'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                              color: '#FFFFFF',
                              fontWeight: 500
                            }}
                          />
                        )}
                      </Stack>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 2,
                        flexWrap: 'wrap'
                      }}>
                        {entry.initialChoice && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.error.main,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            Your Answer: {entry.initialChoice}
                          </Typography>
                        )}
                        {entry.correctAnswer && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.success.main,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            Correct: {entry.correctAnswer}
                          </Typography>
                        )}
                      </Box>
                      {entry.mistakeReasons && entry.mistakeReasons.length > 0 && (
                        <Box sx={{ mt: 'auto' }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            {entry.mistakeReasons.map((reason) => (
                              <Chip 
                                key={reason}
                                label={reason}
                                size={isMobile ? "small" : "medium"}
                                variant="outlined"
                                sx={{
                                  borderColor: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                                  color: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
                                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
        {sortedDates.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: { xs: 4, sm: 6 },
            px: { xs: 2, sm: 3 }
          }}>
            <PsychologyIcon sx={{ 
              fontSize: { xs: 48, sm: 64 }, 
              color: theme.palette.mode === 'dark' ? '#3B82F6' : '#60A5FA',
              mb: 2
            }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                mb: 1
              }}
            >
              No entries found
            </Typography>
            <Typography 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Start adding your wrong answers to track your progress
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  const handleClose = () => {
    setOpenDialog(false);
    setIsEditing(false);
    setEditingEntry(null);
  };

  const handleViewChange = (newView) => {
    setView(newView);
    if (newView === 'calendar') {
      setSelectedDate(new Date());
      setFilteredEntries([]);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          p: { xs: 2, sm: 3 },
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: theme.palette.mode === 'dark' ? 
            'rgba(30, 41, 59, 0.8)' : 
            'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(8px)'
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              flex: 1, 
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#1E293B'
            }}
          >
            Wrong Answer Journal
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsAddingEntry(true)}
              startIcon={<Add />}
              size={isMobile ? "small" : "medium"}
            >
              Add Entry
            </Button>
            <Button
              variant={view === 'list' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('list')}
              startIcon={<ViewList />}
              size={isMobile ? "small" : "medium"}
            >
              List
            </Button>
            <Button
              variant={view === 'calendar' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('calendar')}
              startIcon={<CalendarToday />}
              size={isMobile ? "small" : "medium"}
            >
              Calendar
            </Button>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          display: 'flex',
          gap: { xs: 2, sm: 3 }
        }}>
          {/* Main Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {view === 'list' ? renderListView(filteredEntries) : renderCalendarView()}
          </Box>

          {/* Filter Sidebar - Hidden on mobile */}
          {!isMobile && renderFilterSidebar()}
        </Box>
      </Box>

      {/* Mobile Actions */}
      {isMobile && (
        <>
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => setIsAddingEntry(true)}
          >
            <AddIcon />
          </Fab>
          <Fab
            color="secondary"
            sx={{ position: 'fixed', bottom: 16, right: 88 }}
            onClick={() => setMobileFilterOpen(true)}
          >
            <FilterIcon />
          </Fab>
        </>
      )}

      {/* Add Entry Form */}
      <AddEntryForm
        open={isAddingEntry || isEditing}
        onSubmit={handleAddEntry}
        onClose={() => {
          setIsAddingEntry(false);
          setIsEditing(false);
          setEditingEntry(null);
        }}
        initialData={editingEntry}
        isEditing={isEditing}
      />
    </Box>
  );
};

export default WrongAnswerJournal; 